import React, { useState, useRef, useEffect } from 'react';
import { OmgevingsData } from '../../types';
import styles from './GebiedsanalyseModule.module.css';

interface OmgevingsDataFilterProps {
  activeFilters: Record<string, boolean>;
  onToggle: (key: string) => void;
  data: OmgevingsData | null;
  loading?: boolean;
  voorzieningenCount?: number;
  bedrijvenOsmCount?: number;
  organisatiesCount?: number;
}

const FILTER_CONFIG = [
  { key: 'meldingen', label: 'Meldingen', color: '#ef4444', countKey: 'meldingen' as const },
  { key: 'werkzaamheden', label: 'Werkzaamheden', color: '#f97316', countKey: 'werkzaamheden' as const },
  { key: 'vergunningen', label: 'Vergunningen', color: '#10b981', countKey: 'vergunningen' as const },
];

const GEBIEDSDATA_FILTERS = [
  { key: 'voorzieningen', label: 'Voorzieningen', color: '#8b5cf6' },
  { key: 'bedrijven_osm', label: 'Bedrijven', color: '#f59e0b' },
  { key: 'organisaties', label: 'Organisaties', color: '#06b6d4' },
  { key: 'panden', label: 'Panden per bouwjaar (BAG)', color: '#78716c', uitleg: 'Alle gebouwen uit de Basisregistratie Adressen en Gebouwen (Kadaster), gekleurd naar bouwjaar. Zichtbaar vanaf ingezoomd niveau.' },
];

export const OmgevingsDataFilter: React.FC<OmgevingsDataFilterProps> = ({
  activeFilters,
  onToggle,
  data,
  loading,
  voorzieningenCount = 0,
  bedrijvenOsmCount = 0,
  organisatiesCount = 0,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const gebiedsCounts: Record<string, number> = {
    voorzieningen: voorzieningenCount,
    bedrijven_osm: bedrijvenOsmCount,
    organisaties: organisatiesCount,
  };

  // Count active filters
  const activeCount = Object.values(activeFilters).filter(Boolean).length;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={styles.filterDropdownWrapper} ref={ref}>
      <button
        className={`${styles.filterDropdownTrigger} ${open ? styles.filterDropdownTriggerOpen : ''}`}
        onClick={() => setOpen(prev => !prev)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
          <circle cx="5" cy="12" r="1" /><circle cx="19" cy="12" r="1" />
        </svg>
        Kaartlagen
        {activeCount > 0 && (
          <span className={styles.filterDropdownBadge}>{activeCount}</span>
        )}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className={styles.filterDropdownMenu}>
          {FILTER_CONFIG.map(filter => {
            const count = data?.[filter.countKey]?.length || 0;
            const active = activeFilters[filter.key];
            return (
              <button
                key={filter.key}
                className={`${styles.filterDropdownItem} ${active ? styles.filterDropdownItemActive : ''}`}
                onClick={() => onToggle(filter.key)}
                disabled={loading}
              >
                <span className={styles.omgevingsFilterDot} style={{ backgroundColor: active ? filter.color : '#ccc' }} />
                <span className={styles.filterDropdownLabel}>{filter.label}</span>
                {count > 0 && (
                  <span className={styles.filterDropdownCount} style={{ backgroundColor: active ? filter.color : '#ccc' }}>{count}</span>
                )}
              </button>
            );
          })}

          {GEBIEDSDATA_FILTERS.map(filter => {
            const count = gebiedsCounts[filter.key] || 0;
            const active = activeFilters[filter.key];
            const alwaysShow = filter.key === 'panden';
            if (count === 0 && !alwaysShow) return null;
            return (
              <button
                key={filter.key}
                className={`${styles.filterDropdownItem} ${active ? styles.filterDropdownItemActive : ''}`}
                onClick={() => onToggle(filter.key)}
                title={(filter as { uitleg?: string }).uitleg}
              >
                <span className={styles.omgevingsFilterDot} style={{ backgroundColor: active ? filter.color : '#ccc' }} />
                <span className={styles.filterDropdownLabel}>{filter.label}</span>
                {count > 0 && (
                  <span className={styles.filterDropdownCount} style={{ backgroundColor: active ? filter.color : '#ccc' }}>{count}</span>
                )}
              </button>
            );
          })}

          {loading && <span className={styles.omgevingsFilterLoading} style={{ padding: '8px 12px' }}>Laden...</span>}
        </div>
      )}
    </div>
  );
};
